/**
 * OncoAgent API Client
 * Handles all /api/cases/* endpoints with X-Case-Token injection.
 * Uses fetch for S3 PUT so Content-Type is not modified (must match presigned URL).
 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import type {
  CreateCaseRequest,
  CreateCaseResponse,
  PresignRequest,
  PresignResponse,
  FinalizeRequest,
  FinalizeResponse,
  RunRequest,
  RunResponse,
  DashboardResponse,
  ConsentPurposeResponse,
  ConsentGrantRequest,
  ConsentStatusResponse,
  ReferralCaptureRequest,
  ReferralDoc,
  ReferralPacketPayload,
} from './types';
import { sanitizeFilename } from '../utils/filename';

import { BACKEND_URL } from '@/config/api';

// ============= Token Storage =============

const TOKEN_PREFIX = 'oncoagent_case_token_';

export function getCaseToken(caseId: string): string | null {
  return localStorage.getItem(`${TOKEN_PREFIX}${caseId}`);
}

export function setCaseToken(caseId: string, token: string): void {
  localStorage.setItem(`${TOKEN_PREFIX}${caseId}`, token);
}

export function removeCaseToken(caseId: string): void {
  localStorage.removeItem(`${TOKEN_PREFIX}${caseId}`);
}

// ============= Axios Instance =============

const apiClient: AxiosInstance = axios.create({
  baseURL: `${BACKEND_URL}/api/cases`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to create config with X-Case-Token and optional clinic session
function withCaseToken(caseId: string, config?: AxiosRequestConfig): AxiosRequestConfig {
  const token = getCaseToken(caseId);
  const clinicSession = typeof localStorage !== 'undefined'
    ? localStorage.getItem('oncoagent_clinic_session')
    : null;
  if (!token && !clinicSession) {
    throw new Error(`No case token or clinic session for case: ${caseId}`);
  }
  const headers: Record<string, string> = {
    ...(config?.headers as Record<string, string> | undefined),
  };
  if (token) headers['X-Case-Token'] = token;
  if (clinicSession) headers['X-Clinic-Session'] = clinicSession;
  return {
    ...config,
    headers,
  };
}

// ============= Retry Logic =============

/**
 * Retry wrapper with exponential backoff.
 * Retries up to 3 times with delays: 500ms, 1000ms, 2000ms
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  retries = 3,
  baseDelay = 500
): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    // Don't retry on 4xx errors (client errors)
    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      throw error;
    }
    
    if (retries <= 0) {
      throw error;
    }
    
    const delay = baseDelay * Math.pow(2, 3 - retries);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return withRetry(fn, retries - 1, baseDelay);
  }
}

// ============= API Functions =============

/**
 * Create a new case. No auth required.
 */
export async function createCase(data: CreateCaseRequest): Promise<CreateCaseResponse> {
  return withRetry(async () => {
    const response = await apiClient.post<CreateCaseResponse>('', data);
    // Store token immediately
    setCaseToken(response.data.caseId, response.data.caseToken);
    return response.data;
  });
}

/**
 * Get dashboard data for a case. Requires X-Case-Token.
 */
export async function getDashboard(caseId: string): Promise<DashboardResponse> {
  return withRetry(async () => {
    const response = await apiClient.get<DashboardResponse>(
      `/${caseId}/dashboard`,
      withCaseToken(caseId)
    );
    return response.data;
  });
}

/**
 * Get presigned URL for document upload. Requires X-Case-Token.
 */
export async function presignDocument(
  caseId: string,
  data: PresignRequest
): Promise<PresignResponse> {
  const response = await apiClient.post<PresignResponse>(
    `/${caseId}/documents/presign`,
    data,
    withCaseToken(caseId)
  );
  return response.data;
}

/**
 * Upload file directly to S3 using presigned URL.
 * Uses fetch so Content-Type is sent exactly as signed (axios can alter headers).
 */
export async function uploadToS3(uploadUrl: string, file: File): Promise<void> {
  const contentType = file.type || 'application/octet-stream';
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!res.ok) {
    throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
  }
}

/**
 * Finalize document upload. Requires X-Case-Token.
 * Requires consent for purpose 'care_navigation' (grant via postConsent).
 */
export async function finalizeDocument(
  caseId: string,
  docId: string,
  data: FinalizeRequest
): Promise<FinalizeResponse> {
  const response = await apiClient.post<FinalizeResponse>(
    `/${caseId}/documents/${docId}/finalize`,
    data,
    withCaseToken(caseId)
  );
  return response.data;
}

/**
 * Get consent status for a single purpose. Requires X-Case-Token.
 * Maps to GET /{caseId}/consent/status (backend source of truth).
 */
export async function getConsentForPurpose(
  caseId: string,
  purpose: string
): Promise<ConsentPurposeResponse> {
  const response = await apiClient.get<ConsentStatusResponse>(
    `/${caseId}/consent/status`,
    withCaseToken(caseId)
  );
  const data = response.data;
  const purposes = data.purposes || [];
  const granted =
    Boolean(data.has_consent) &&
    (purposes.length === 0 || purposes.includes(purpose));
  return {
    caseId,
    purpose,
    granted,
    grantedAt: data.granted_at ?? null,
  };
}

/**
 * Grant consent for one or more purposes. Requires X-Case-Token.
 * Maps to POST /{caseId}/consent/grant.
 */
export async function postConsent(
  caseId: string,
  data: { purpose: string; granted: boolean; version?: string } | ConsentGrantRequest
): Promise<ConsentPurposeResponse> {
  const purposes =
    'purposes' in data
      ? data.purposes
      : data.granted
        ? [data.purpose]
        : [];

  if (!purposes.length) {
    throw new Error('Consent grant requires at least one purpose');
  }

  const version =
    'consent_version' in data
      ? data.consent_version
      : 'version' in data
        ? data.version
        : 'v1.0';

  const response = await apiClient.post<{
    consentId: string;
    status: string;
    purposes: string[];
    grantedAt?: string;
  }>(
    `/${caseId}/consent/grant`,
    {
      purposes,
      subject_type: 'patient',
      consent_version: version || 'v1.0',
    } satisfies ConsentGrantRequest,
    withCaseToken(caseId)
  );

  return {
    caseId,
    purpose: purposes[0],
    granted: response.data.status === 'GRANTED' || true,
    grantedAt: response.data.grantedAt ?? null,
  };
}

/**
 * Run care manager (FAST mode). Requires X-Case-Token.
 */
export async function runCareManager(
  caseId: string,
  data: RunRequest = { mode: 'fast' }
): Promise<RunResponse> {
  return withRetry(async () => {
    const response = await apiClient.post<RunResponse>(
      `/${caseId}/run`,
      data,
      withCaseToken(caseId)
    );
    return response.data;
  });
}

/**
 * Full upload flow: presign → PUT → finalize.
 * Sanitizes filename before presign so backend accepts it (e.g. "Screenshot 2026-01-31 110043.png" → safe name).
 */
export async function uploadDocument(
  caseId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<FinalizeResponse> {
  return withRetry(async () => {
    const safeFilename = sanitizeFilename(file.name);
    const contentType = file.type || 'application/octet-stream';

    // 1. Get presigned URL (send sanitized filename so backend validation passes)
    const presignResult = await presignDocument(caseId, {
      filename: safeFilename,
      content_type: contentType,
      file_size_bytes: file.size,
    });

    // 2. Upload to S3 with fetch; Content-Type must match what backend signed
    const res = await fetch(presignResult.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!res.ok) {
      throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
    }
    if (onProgress) onProgress(100);

    // 3. Finalize
    const finalizeResult = await finalizeDocument(caseId, presignResult.docId, {
      key: presignResult.storageKey,
    });

    return finalizeResult;
  });
}

/**
 * Generate shareable case link with token.
 */
export function getCaseLink(caseId: string): string {
  const token = getCaseToken(caseId);
  if (!token) {
    throw new Error(`No case token found for case: ${caseId}`);
  }
  const baseUrl = window.location.origin;
  return `${baseUrl}/oncoagent/case/${caseId}?token=${encodeURIComponent(token)}`;
}

/**
 * Extract token from URL and store it.
 */
export function restoreTokenFromUrl(caseId: string): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  if (token) {
    setCaseToken(caseId, token);
    // Clean up URL (remove token param)
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('token');
    window.history.replaceState({}, '', newUrl.toString());
    return true;
  }
  return false;
}

export async function saveReferralCapture(
  caseId: string,
  data: ReferralCaptureRequest
): Promise<{
  referralId: string;
  status: string;
  route: ReferralDoc['route'];
  redFlags: ReferralDoc['red_flags'];
  clarifyPrompts: ReferralDoc['clarify_prompts'];
  clarifyGate?: {
    can_submit: boolean;
    blocking: Array<{ why: string; suggestion: string; taskKey?: string; priority?: string }>;
    warnings: Array<{ why: string; suggestion: string; taskKey?: string; priority?: string }>;
    blocking_count: number;
    warning_count: number;
  };
}> {
  const response = await apiClient.post(`/${caseId}/referral`, data, withCaseToken(caseId));
  return response.data;
}

export async function getReferral(caseId: string): Promise<ReferralDoc> {
  const response = await apiClient.get<ReferralDoc>(
    `/${caseId}/referral`,
    withCaseToken(caseId)
  );
  return response.data;
}

export async function generateReferralPacket(
  caseId: string
): Promise<{ packet: ReferralPacketPayload }> {
  const response = await apiClient.post(
    `/${caseId}/referral/packet`,
    {},
    withCaseToken(caseId)
  );
  return response.data;
}

export async function submitReferral(
  caseId: string,
  data?: {
    destination_clinic_id?: string;
    acknowledge_incomplete?: boolean;
    acknowledge_reason?: string;
    phi_handoff_consent?: boolean;
  }
): Promise<{
  referralId: string;
  status: string;
  slaDeadline?: string;
  route?: ReferralDoc['route'];
  packet?: ReferralPacketPayload;
  tracking?: {
    tracking_id?: string;
    share_token?: string;
    current_milestone?: string;
  };
  destination_clinic_id?: string;
  destination_clinic_name?: string;
}> {
  const response = await apiClient.post(
    `/${caseId}/referral/submit`,
    data || {},
    withCaseToken(caseId)
  );
  return response.data;
}

export async function openReferralFromInbox(referralId: string): Promise<{
  case_id: string;
  case_token: string;
  referral_id: string;
  expires_in_seconds: number;
  status?: string;
  packet?: ReferralPacketPayload;
  tracking?: any;
  route?: ReferralDoc['route'];
}> {
  const session = getClinicSession();
  if (!session) {
    throw new Error('Clinic session required');
  }
  const response = await axios.post(
    `${BACKEND_URL}/api/referrals/${referralId}/open`,
    {},
    {
      withCredentials: true,
      headers: { 'X-Clinic-Session': session },
    }
  );
  const data = response.data;
  if (data?.case_id && data?.case_token) {
    setCaseToken(data.case_id, data.case_token);
  }
  return data;
}

export async function fetchReferralInbox(params?: {
  specialty?: string;
  status?: string;
  vertical?: string;
  scope?: 'inbox' | 'sent' | 'all';
}): Promise<{ items: ReferralDoc[]; count: number; clinic_id?: string; scope?: string }> {
  const session = getClinicSession();
  if (!session) {
    throw new Error('Clinic session required — register or join a clinic first');
  }
  const response = await axios.get(`${BACKEND_URL}/api/referrals/inbox`, {
    params,
    withCredentials: true,
    headers: { 'X-Clinic-Session': session },
  });
  return response.data;
}

export async function updateReferralInboxStatus(
  referralId: string,
  statusValue: string
): Promise<ReferralDoc> {
  const session = getClinicSession();
  if (!session) {
    throw new Error('Clinic session required');
  }
  const response = await axios.patch(
    `${BACKEND_URL}/api/referrals/${referralId}/status`,
    { status: statusValue },
    {
      withCredentials: true,
      headers: { 'X-Clinic-Session': session },
    }
  );
  return response.data;
}

export async function postReferralTransition(
  referralId: string,
  transition: string,
  payload?: {
    notes?: string;
    assignee_id?: string;
    destination_clinic_id?: string;
    information_request_description?: string;
    acknowledge_incomplete?: boolean;
    acknowledge_reason?: string;
  }
): Promise<ReferralDoc> {
  const session = getClinicSession();
  if (!session) {
    throw new Error('Clinic session required');
  }
  const response = await axios.post(
    `${BACKEND_URL}/api/referrals/${referralId}/transitions`,
    {
      transition,
      ...payload,
    },
    {
      withCredentials: true,
      headers: { 'X-Clinic-Session': session },
    }
  );
  return response.data;
}

// ============= Clinic org + protocols + delivery (Phase C/D) =============

const CLINIC_SESSION_KEY = 'oncoagent_clinic_session';
const CLINIC_ID_KEY = 'oncoagent_clinic_id';
const CLINIC_PROFILES_KEY = 'oncoagent_clinic_demo_profiles';

export type ClinicDemoProfile = {
  clinic_id: string;
  clinic_name?: string;
  session_token: string;
  member_email?: string;
  role?: string;
  saved_at: string;
};

export function getClinicSession(): string | null {
  return localStorage.getItem(CLINIC_SESSION_KEY);
}

export function setClinicSession(token: string): void {
  localStorage.setItem(CLINIC_SESSION_KEY, token);
}

export function clearClinicSession(): void {
  localStorage.removeItem(CLINIC_SESSION_KEY);
  localStorage.removeItem(CLINIC_ID_KEY);
}

export function getClinicId(): string | null {
  return localStorage.getItem(CLINIC_ID_KEY);
}

export function setClinicId(id: string): void {
  localStorage.setItem(CLINIC_ID_KEY, id);
}

/** Same-browser demo: remember up to 4 clinic sessions for one-click switch. */
export function listClinicDemoProfiles(): ClinicDemoProfile[] {
  try {
    const raw = localStorage.getItem(CLINIC_PROFILES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function rememberClinicDemoProfile(profile: Omit<ClinicDemoProfile, 'saved_at'>): void {
  const next: ClinicDemoProfile = { ...profile, saved_at: new Date().toISOString() };
  const others = listClinicDemoProfiles().filter((p) => p.clinic_id !== next.clinic_id);
  localStorage.setItem(CLINIC_PROFILES_KEY, JSON.stringify([next, ...others].slice(0, 4)));
}

export function switchClinicDemoProfile(clinicId: string): ClinicDemoProfile | null {
  const hit = listClinicDemoProfiles().find((p) => p.clinic_id === clinicId);
  if (!hit?.session_token) return null;
  setClinicSession(hit.session_token);
  setClinicId(hit.clinic_id);
  return hit;
}

function clinicHeaders(): Record<string, string> {
  const session = getClinicSession();
  return session ? { 'X-Clinic-Session': session } : {};
}

export async function registerClinic(data: {
  name: string;
  admin_name: string;
  admin_email: string;
  password?: string;
  city?: string;
  state?: string;
  npi_org?: string;
  specialties?: string[];
  mode?: 'referring' | 'receiving' | 'both';
  network_id?: string;
  accepts_referrals?: boolean;
}): Promise<any> {
  const response = await axios.post(`${BACKEND_URL}/api/oncoagent/clinic/register`, data, {
    withCredentials: true,
  });
  return response.data;
}

export async function joinClinic(data: {
  invite_code: string;
  name: string;
  email: string;
  role?: string;
  password?: string;
}): Promise<any> {
  const response = await axios.post(`${BACKEND_URL}/api/oncoagent/clinic/join`, data, {
    withCredentials: true,
  });
  return response.data;
}

export async function loginClinic(data: {
  email: string;
  password: string;
  clinic_id?: string;
  mfa_code?: string;
}): Promise<any> {
  const response = await axios.post(`${BACKEND_URL}/api/oncoagent/clinic/login`, data, {
    withCredentials: true,
  });
  return response.data;
}

export async function requestClinicMagicLink(data: {
  email: string;
  clinic_id?: string;
}): Promise<any> {
  const response = await axios.post(`${BACKEND_URL}/api/oncoagent/clinic/magic-link`, data, {
    withCredentials: true,
  });
  return response.data;
}

export async function consumeClinicMagicLink(token: string): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/magic-link/consume`,
    { token },
    { withCredentials: true }
  );
  return response.data;
}

export async function requestClinicPasswordReset(data: {
  email: string;
  clinic_id?: string;
}): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/password/reset-request`,
    data,
    { withCredentials: true }
  );
  return response.data;
}

export async function resetClinicPassword(token: string, newPassword: string): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/password/reset`,
    { token, new_password: newPassword },
    { withCredentials: true }
  );
  return response.data;
}

export async function setClinicPassword(password: string, currentPassword?: string): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/password/set`,
    { password, current_password: currentPassword },
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function refreshClinicSession(): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/session/refresh`,
    {},
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function lookupClinicMemberships(email: string): Promise<{
  items: Array<{
    clinic_id: string;
    clinic_name?: string;
    role?: string;
    password_set?: boolean;
    sso_enabled?: boolean;
  }>;
  count: number;
}> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/memberships/lookup`,
    { email },
    { withCredentials: true }
  );
  return response.data;
}

export async function fetchClinicAnalytics(days = 30): Promise<any> {
  const response = await axios.get(`${BACKEND_URL}/api/oncoagent/clinic/analytics`, {
    params: { days },
    headers: clinicHeaders(),
    withCredentials: true,
  });
  return response.data;
}

export async function logoutClinic(): Promise<void> {
  const session = getClinicSession();
  try {
    if (session) {
      await axios.post(
        `${BACKEND_URL}/api/oncoagent/clinic/logout`,
        {},
        { headers: clinicHeaders(), withCredentials: true }
      );
    }
  } finally {
    clearClinicSession();
  }
}

export async function getClinicMe(): Promise<any> {
  const response = await axios.get(`${BACKEND_URL}/api/oncoagent/clinic/me`, {
    headers: clinicHeaders(),
    withCredentials: true,
  });
  return response.data;
}

export async function configureClinicSso(data: {
  provider: string;
  issuer: string;
  client_id: string;
  client_secret?: string;
  enabled?: boolean;
}): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/sso/configure`,
    data,
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function rotateClinicInvite(): Promise<{
  invite_code: string;
  invite_expires_at: string;
}> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/invite/rotate`,
    {},
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function emailClinicInvite(email: string): Promise<{
  ok: boolean;
  email_delivery: string;
  invite_code?: string;
  message?: string;
}> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/invite/email`,
    { email },
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function verifyClinicMail(): Promise<{
  ok: boolean;
  enabled?: boolean;
  from_email?: string;
  host?: string;
  error?: string;
}> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/mail/verify`,
    {},
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function rotateClinicApiKey(): Promise<{ api_key: string; api_key_prefix: string }> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/api-key/rotate`,
    {},
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function updateClinicMemberRole(
  memberId: string,
  role: string
): Promise<{ member: any }> {
  const response = await axios.patch(
    `${BACKEND_URL}/api/oncoagent/clinic/members/${memberId}/role`,
    { role },
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function updateClinicMemberStatus(
  memberId: string,
  statusValue: 'active' | 'disabled'
): Promise<{ member: any }> {
  const response = await axios.patch(
    `${BACKEND_URL}/api/oncoagent/clinic/members/${memberId}/status`,
    { status: statusValue },
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function updateClinicSettings(data: {
  mode?: 'referring' | 'receiving' | 'both';
  accepts_referrals?: boolean;
  network_id?: string;
  specialties?: string[];
  default_fax?: string;
  ehr_vendor?: string;
  ehr_endpoint?: string;
  direct_enabled?: boolean;
}): Promise<any> {
  const response = await axios.patch(
    `${BACKEND_URL}/api/oncoagent/clinic/settings`,
    data,
    { headers: clinicHeaders(), withCredentials: true }
  );
  return response.data;
}

export async function fetchClinicDirectory(params?: {
  q?: string;
  specialty?: string;
  vertical?: string;
  network_id?: string;
}): Promise<{ items: Array<{ clinic_id: string; name: string; city?: string; state?: string; specialties?: string[] }>; count: number }> {
  const response = await axios.get(`${BACKEND_URL}/api/oncoagent/clinic/directory`, {
    params,
    headers: clinicHeaders(),
    withCredentials: true,
  });
  return response.data;
}

export async function startClinicSso(clinicId: string, redirectUri: string): Promise<{ authorize_url: string }> {
  const response = await axios.post(
    `${BACKEND_URL}/api/oncoagent/clinic/sso/start`,
    { clinic_id: clinicId, redirect_uri: redirectUri },
    { withCredentials: true }
  );
  return response.data;
}

export async function fetchProtocol(vertical: string): Promise<{ protocol: any }> {
  const response = await axios.get(`${BACKEND_URL}/api/oncoagent/protocols`, {
    params: { vertical },
  });
  return response.data;
}

export async function fetchProtocolCaptureSchema(vertical: string, specialty?: string): Promise<{
  vertical: string;
  fields: Array<{
    key: string;
    label: string;
    required?: boolean;
    field_type?: string;
    placeholder?: string;
    options?: string[];
  }>;
}> {
  const response = await axios.get(
    `${BACKEND_URL}/api/oncoagent/protocols/${vertical}/capture-schema`,
    { params: { specialty } }
  );
  return response.data;
}

export async function pushReferralFhir(
  caseId: string,
  endpoint?: string
): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/referral/fhir/push`,
    null,
    {
      ...withCaseToken(caseId),
      params: endpoint ? { endpoint } : undefined,
    }
  );
  return response.data;
}

export async function fetchPathways(vertical?: string): Promise<any> {
  const response = await axios.get(`${BACKEND_URL}/api/oncoagent/pathways`, {
    params: vertical ? { vertical } : undefined,
  });
  return response.data;
}

export async function downloadReferralFhir(
  caseId: string
): Promise<{ bundle: Record<string, unknown>; format: string }> {
  const response = await apiClient.get(`/${caseId}/referral/fhir`, withCaseToken(caseId));
  return response.data;
}

export async function sendReferralFax(
  caseId: string,
  toNumber: string,
  clinicId?: string
): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/referral/fax`,
    { to_number: toNumber, clinic_id: clinicId },
    withCaseToken(caseId)
  );
  return response.data;
}

export async function queueReferralDirect(
  caseId: string,
  toDirectAddress: string,
  clinicId?: string
): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/referral/direct`,
    { to_direct_address: toDirectAddress, clinic_id: clinicId },
    withCaseToken(caseId)
  );
  return response.data;
}

// ============= Patient tracking / delay reduction =============

export async function fetchCaseTracking(caseId: string): Promise<any> {
  const response = await apiClient.get(`/${caseId}/tracking`, withCaseToken(caseId));
  return response.data;
}

export async function fetchPublicTracking(
  shareToken: string,
  mode?: 'clinic' | string
): Promise<any> {
  const response = await axios.get(`${BACKEND_URL}/api/tracking/${shareToken}`, {
    params: mode ? { mode } : undefined,
  });
  return response.data;
}

export async function confirmPublicTracking(shareToken: string): Promise<any> {
  const response = await axios.post(`${BACKEND_URL}/api/tracking/${shareToken}/confirm`, {
    actor: 'patient',
  });
  return response.data;
}

export async function savePatientNotifyEmail(
  shareToken: string,
  patientEmail: string
): Promise<any> {
  const response = await axios.post(`${BACKEND_URL}/api/tracking/${shareToken}/notify-email`, {
    patient_email: patientEmail,
  });
  return response.data;
}

export async function updateCaseNotifyContacts(
  caseId: string,
  data: { patient_email?: string; clinician_email?: string; clinic_email?: string }
): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/notify`,
    data,
    withCaseToken(caseId)
  );
  return response.data;
}

export async function scheduleCaseAppointment(
  caseId: string,
  data: { scheduled_for: string; location?: string; specialty?: string; notes?: string }
): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/schedule`,
    data,
    withCaseToken(caseId)
  );
  return response.data;
}

export async function logCasePatientContact(
  caseId: string,
  data: { channel?: string; outcome?: string; notes?: string }
): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/contact`,
    data,
    withCaseToken(caseId)
  );
  return response.data;
}

export async function confirmCaseAppointment(caseId: string): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/confirm`,
    { actor: 'clinic' },
    withCaseToken(caseId)
  );
  return response.data;
}

export async function completeCaseConsult(caseId: string, notes?: string): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/complete`,
    { notes: notes || '' },
    withCaseToken(caseId)
  );
  return response.data;
}

export async function rescheduleCaseAppointment(
  caseId: string,
  data: { scheduled_for: string; location?: string; notes?: string }
): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/reschedule`,
    data,
    withCaseToken(caseId)
  );
  return response.data;
}

export async function markCaseNoShow(caseId: string, notes?: string): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/no-show`,
    { notes: notes || '' },
    withCaseToken(caseId)
  );
  return response.data;
}

export async function cancelCaseAppointment(caseId: string, notes?: string): Promise<any> {
  const response = await apiClient.post(
    `/${caseId}/tracking/cancel-appointment`,
    { notes: notes || '' },
    withCaseToken(caseId)
  );
  return response.data;
}

export async function scheduleReferralAppointment(
  referralId: string,
  data: { scheduled_for: string; location?: string; specialty?: string; notes?: string }
): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/referrals/${referralId}/tracking/schedule`,
    data,
    { withCredentials: true, headers: clinicHeaders() }
  );
  return response.data;
}

export async function logReferralPatientContact(
  referralId: string,
  data: { channel?: string; outcome?: string; notes?: string }
): Promise<any> {
  const response = await axios.post(
    `${BACKEND_URL}/api/referrals/${referralId}/tracking/contact`,
    data,
    { withCredentials: true, headers: clinicHeaders() }
  );
  return response.data;
}

export async function fetchTrackingAnalytics(): Promise<any> {
  const response = await axios.get(`${BACKEND_URL}/api/referrals/tracking/analytics`, {
    withCredentials: true,
    headers: clinicHeaders(),
  });
  return response.data;
}

export async function fetchTrackingBreaches(): Promise<{ items: any[]; count: number }> {
  const response = await axios.get(`${BACKEND_URL}/api/referrals/tracking/breaches`, {
    withCredentials: true,
    headers: clinicHeaders(),
  });
  return response.data;
}

export async function fetchExtractions(caseId: string): Promise<any[]> {
  const response = await apiClient.get(`/${caseId}/extractions`, withCaseToken(caseId));
  return response.data;
}

export async function confirmExtractionField(
  caseId: string,
  data: { file_id: string; field: string; value: any }
): Promise<any> {
  const response = await apiClient.post(`/${caseId}/extractions/confirm`, data, withCaseToken(caseId));
  return response.data;
}

// Export client for testing
export { apiClient };
